import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Send as SendIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface Invite {
  _id: string;
  email: string;
  token: string;
  kindergarten: {
    _id: string;
    name: string;
  };
  child?: {
    _id: string;
    fullName: string;
  };
  status: 'pending' | 'accepted' | 'expired';
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

interface Kindergarten {
  _id: string;
  name: string;
}

interface Child {
  _id: string;
  fullName: string;
}

interface InviteFormData {
  email: string;
  kindergartenId: string;
  childId?: string;
  expiresIn: number; // днів
}

const Invites: React.FC = () => {
  const { hasPermission } = useAdminAuth();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [kindergartens, setKindergartens] = useState<Kindergarten[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingInvite, setEditingInvite] = useState<Invite | null>(null);
  const [formData, setFormData] = useState<InviteFormData>({
    email: '',
    kindergartenId: '',
    childId: '',
    expiresIn: 7,
  });

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invitesRes, kindergartensRes, childrenRes] = await Promise.all([
        api.get('/invites'),
        api.get('/kindergartens'),
        api.get('/children'),
      ]);
      
      setInvites(invitesRes.data.data || []);
      setKindergartens(kindergartensRes.data.data || []);
      setChildren(childrenRes.data.data || []);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (invite?: Invite) => {
    if (invite) {
      setEditingInvite(invite);
      setFormData({
        email: invite.email,
        kindergartenId: invite.kindergarten._id,
        childId: invite.child?._id || '',
        expiresIn: 7,
      });
    } else {
      setEditingInvite(null);
      setFormData({
        email: '',
        kindergartenId: '',
        childId: '',
        expiresIn: 7,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingInvite(null);
    setFormData({
      email: '',
      kindergartenId: '',
      childId: '',
      expiresIn: 7,
    });
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('email', formData.email);
      formDataToSend.append('kindergartenId', formData.kindergartenId);
      if (formData.childId) {
        formDataToSend.append('childId', formData.childId);
      }
      formDataToSend.append('expiresIn', formData.expiresIn.toString());

      if (editingInvite) {
        await api.put(`/invites/${editingInvite._id}`, formDataToSend);
      } else {
        await api.post('/invites', formDataToSend);
      }

      handleCloseDialog();
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка збереження');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Ви впевнені, що хочете видалити цей інвайт?')) {
      try {
        await api.delete(`/invites/${id}`);
        fetchData();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Помилка видалення');
      }
    }
  };

  const handleResend = async (invite: Invite) => {
    try {
      await api.post(`/invites/${invite._id}/resend`);
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка повторного відправлення');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleIcon color="success" />;
      case 'expired':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <ScheduleIcon color="warning" />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'success';
      case 'expired':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'Прийнято';
      case 'expired':
        return 'Протерміновано';
      case 'pending':
        return 'Очікує';
      default:
        return 'Невідомо';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Інвайти
        </Typography>
        {hasPermission('invites', 'create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => handleOpenDialog()}
          >
            Відправити інвайт
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Дитячий садок</TableCell>
                  <TableCell>Дитина</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Термін дії</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invites.map((invite) => (
                  <TableRow key={invite._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {invite.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" color="action" />
                        {invite.kindergarten.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {invite.child ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          {invite.child.fullName}
                        </Box>
                      ) : (
                        <Chip label="Не вказана" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon(invite.status)}
                        <Chip 
                          label={getStatusText(invite.status)} 
                          size="small" 
                          color={getStatusColor(invite.status) as any}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={isExpired(invite.expiresAt) ? 'error' : 'text.secondary'}
                      >
                        {formatDate(invite.expiresAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Переглянути">
                          <IconButton size="small" color="primary">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('invites', 'update') && (
                          <Tooltip title="Редагувати">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog(invite)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {invite.status === 'pending' && hasPermission('invites', 'update') && (
                          <Tooltip title="Повторно відправити">
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handleResend(invite)}
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('invites', 'delete') && (
                          <Tooltip title="Видалити">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(invite._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог додавання/редагування */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingInvite ? 'Редагувати інвайт' : 'Відправити інвайт'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Email батька"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              margin="normal"
              required
              helperText="Email адреса батька для відправки інвайту"
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Дитячий садок</InputLabel>
              <Select
                value={formData.kindergartenId}
                onChange={(e) => setFormData(prev => ({ ...prev, kindergartenId: e.target.value }))}
                label="Дитячий садок"
              >
                {kindergartens.map((kindergarten) => (
                  <MenuItem key={kindergarten._id} value={kindergarten._id}>
                    {kindergarten.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Дитина (необов'язково)</InputLabel>
              <Select
                value={formData.childId}
                onChange={(e) => setFormData(prev => ({ ...prev, childId: e.target.value }))}
                label="Дитина (необов'язково)"
              >
                <MenuItem value="">
                  <em>Не вказана</em>
                </MenuItem>
                {children.map((child) => (
                  <MenuItem key={child._id} value={child._id}>
                    {child.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Термін дії (днів)"
              type="number"
              value={formData.expiresIn}
              onChange={(e) => setFormData(prev => ({ ...prev, expiresIn: parseInt(e.target.value) || 7 }))}
              margin="normal"
              required
              helperText="Кількість днів, протягом яких інвайт буде дійсним"
              inputProps={{ min: 1, max: 30 }}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Інвайт буде відправлений на вказану email адресу. Батько зможе зареєструватися в мобільному додатку 
              та отримати доступ до інформації про свою дитину.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingInvite ? 'Зберегти' : 'Відправити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invites; 